/* eslint-disable @typescript-eslint/no-shadow */
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { FocusEvent, useState } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import DialogPopover from '../DialogPopover/DialogPopover';
import { DialogText, DialogTitle } from '../../constants/changes';
import { DialogType } from '../../types/changes';
import { useChangeStore } from '../../store/changeStore';
import LocalizationWrapper from '../Date/LocalizationWrapper';

type ConfirmChangeDialogProps = {
  isOpen: boolean;
  modalType: DialogType;
  isLoading: boolean;
  handleClose: () => void;
  handleSubmit: (
    comment: string,
    reasonToChange: string,
    processIdList: number[],
  ) => void;
};

const ReworkImplemetationDialog = observer(
  ({
    isOpen,
    modalType,
    isLoading,
    handleClose,
    handleSubmit,
  }: ConfirmChangeDialogProps) => {
    const [reasonToChange, setReasonToChange] = useState('');
    const [value, setValue] = useState<Date | null>(null);
    const [isDesktopDatePickerOpen, setIsDesktopDatePickerOpen] =
      useState(false);

    const [processList, setProcessList] = useState<number[]>([]);

    const { t } = useTranslation();
    const titles = DialogTitle();
    const texts = DialogText();

    const { change } = useChangeStore();

    const onChangeHandler = (newVal: Date | null) => {
      setValue(newVal);
    };

    const onSubmitHandler = () => {
      if (value)
        handleSubmit(value.toDateString(), reasonToChange, processList);
    };

    const onDesktopDatePickerFocus = (
      e: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>,
    ) => {
      setIsDesktopDatePickerOpen(true);
      e.target.blur();
    };

    const addOrRemoveCheckbox = (processId: number) => {
      if (!processList.includes(processId))
        setProcessList(processList => [...processList, processId]);
      else {
        setProcessList(() =>
          processList.filter(process => process !== processId),
        );
      }
    };

    const addedProcesses =
      change?.commonProject?.navProcessToCommonProjectRelations.filter(
        process => change.processes.some(p => p.pId === process.id),
      );

    return (
      <DialogPopover
        isOpen={isOpen}
        isDisabledConfirm={!value}
        title={titles[modalType]}
        contentText={texts[modalType]}
        handleClose={handleClose}
        handleSubmit={onSubmitHandler}
        isLoading={isLoading}
      >
        <TextField
          error={!(reasonToChange ? !!reasonToChange.length : undefined)}
          css={css`
            .MuiInput-underline:before {
              border-bottom: 1px solid #ececec;
            }

            .MuiInputLabel-asterisk {
              color: #ff0000;
            }
          `}
          required
          autoFocus
          margin="dense"
          multiline
          id="name"
          label={t('dialog.ReasonToChange')}
          type="text"
          fullWidth
          value={reasonToChange}
          variant="standard"
          onChange={e => setReasonToChange(e.target.value.toString())}
        />

        <Typography variant="h5" sx={{ fontSize: 14, marginTop: 2 }}>
          {t('sendBackToKTM')}
        </Typography>
        {addedProcesses &&
          addedProcesses
            .slice()
            .sort((a, b) => Number(a.groupNo) - Number(b.groupNo))
            .map(prProcess => {
              const processID =
                change?.processes.find(process => process.pId === prProcess.id)
                  ?.id ?? 0;

              return (
                <FormGroup key={prProcess.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={processList.includes(processID)}
                        onChange={() => addOrRemoveCheckbox(processID)}
                        css={css`
                          svg {
                            color: #00223b;
                          }
                        `}
                      />
                    }
                    value={prProcess.groupNo}
                    label={`${prProcess.groupNo} | ${prProcess.owner}`}
                    css={css`
                      .MuiTypography-root {
                        font-size: 14px;
                        font-weight: 300;
                        color: #333333;
                      }
                    `}
                  />
                </FormGroup>
              );
            })}

        <LocalizationWrapper>
          <DesktopDatePicker
            open={isDesktopDatePickerOpen}
            onOpen={() => setIsDesktopDatePickerOpen(true)}
            onClose={() => setIsDesktopDatePickerOpen(false)}
            format="dd.MM.yyyy"
            label={t('reworkImplementation')}
            value={value}
            onChange={onChangeHandler}
            slotProps={{
              textField: {
                style: { width: '100%', marginTop: 16 },
                variant: 'standard',
                error: !value,
                autoComplete: 'off',
                InputProps: {
                  onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                    onDesktopDatePickerFocus(e),
                  sx: {
                    marginTop: 3,
                    paddingLeft: 1,
                    '& .MuiSvgIcon-root': {
                      color: '#80acd4',
                    },
                  },
                },
                InputLabelProps: { sx: { paddingLeft: 1 } },
              },
              inputAdornment: { sx: { paddingRight: 1 } },
            }}
          />
        </LocalizationWrapper>
      </DialogPopover>
    );
  },
);

export default ReworkImplemetationDialog;
